// File: app/src/components/workout/freeTrainingParts.js
//
// Pieces Free training's log (#157) and its checklist (#164) share: the
// kinds' icon and colours, and the number steppers.

import React from "react";
import { Dumbbell, Heart, Zap } from "lucide-react";
import DraggableStepper from "../ui/draggable-stepper";
import { ChoiceChip } from "../profile/ProfileParts";
import { AmountStepper, UnitChoice, WeightLoadField, loadStepFor } from "../plan/StepAmountFields";
import { formatDistance } from "../../utils/freeTrainingLog";

// Each kind's icon. Everything logged is work, so it all takes the Coach's
// work colour: blue would read as rest and amber as warm-up.
export const KIND = {
  lift: { icon: Dumbbell, text: "text-work", rail: "bg-work" },
  body: { icon: Dumbbell, text: "text-work", rail: "bg-work" },
  cardio: { icon: Heart, text: "text-work", rail: "bg-work" },
  block: { icon: Zap, text: "text-work", rail: "bg-work" },
};
export const kindStyle = (kind) => KIND[kind] || KIND.lift;

export const weightStep = loadStepFor;

// ─── Steppers ───────────────────────────────────────────────────────────────
// The plan editor's own amount steppers (plan/StepAmountFields), so a number
// is set the same way in the log as in the step dialog. Distance is the one
// extra: in a plan it rides in the step's name ("Row 2 km").

export const WeightStepper = ({ name, value, onChange }) => (
  <WeightLoadField name={name} value={value} onChange={onChange} offLabel="BW" />
);

export const RepsStepper = ({ value, onChange }) => <AmountStepper unit="reps" value={value} onChange={onChange} />;

export const DistanceStepper = ({ value, onChange }) => (
  <DraggableStepper
    label="Distance"
    value={value}
    onChange={onChange}
    min={100}
    max={42200}
    step={100}
    unit="km"
    formatValue={(v) => (v / 1000).toFixed(1)}
    parseValue={(km) => km * 1000}
    momentum
  />
);

export const TimeStepper = ({ value, onChange }) => <AmountStepper unit="s" value={value} onChange={onChange} />;

export const RoundsStepper = ({ name, value, onChange }) => (
  <AmountStepper unit="rounds" label={name === "EMOM" ? "Minutes" : "Rounds"} value={value} onChange={onChange} />
);

// A hold's set: seconds.
export const HoldStepper = TimeStepper;

export const SetsStepper = ({ value, onChange }) => <AmountStepper unit="sets" value={value} onChange={onChange} />;

export const CaloriesStepper = ({ value, onChange }) => <AmountStepper unit="cal" value={value} onChange={onChange} />;

const PIECE_LABELS = { distance: "Distance", time: "Time", cal: "Calories" };
const PIECE_PRESETS = {
  distance: [500, 1000, 2000, 5000, 10000, 21100],
  time: [300, 600, 900, 1200, 1800, 3600],
  cal: [10, 15, 20, 30, 50, 100],
};
const presetLabel = (metric, v) =>
  metric === "distance" ? formatDistance(v) : metric === "time" ? `${v / 60} min` : `${v} cal`;

/**
 * A cardio piece's numbers: by distance, time or calories (Assault Bike, ski
 * erg…), with quick picks when `presets` and the stepper. `piece` is
 * { metric, m, s, cal }; `onChange` gets the fields that changed.
 */
export const PieceFields = ({ piece, onChange, presets = false }) => {
  const metric = piece.metric || "distance";
  const value = { distance: piece.m || 1000, time: piece.s || 600, cal: piece.cal || 20 };
  const field = { distance: "m", time: "s", cal: "cal" };
  const set = (m, v) => onChange({ metric: m, [field[m]]: v });
  return (
    <>
      <UnitChoice units={["distance", "time", "cal"]} labels={PIECE_LABELS} value={metric} onChange={(m) => set(m, value[m])} />
      {presets && (
        <div className="grid grid-cols-3 gap-1.5">
          {PIECE_PRESETS[metric].map((v) => (
            <ChoiceChip key={v} selected={value[metric] === v} onClick={() => set(metric, v)} className="rounded-lg">
              {presetLabel(metric, v)}
            </ChoiceChip>
          ))}
        </div>
      )}
      {metric === "distance" ? (
        <DistanceStepper value={value.distance} onChange={(v) => set("distance", v)} />
      ) : metric === "time" ? (
        <TimeStepper value={value.time} onChange={(v) => set("time", v)} />
      ) : (
        <CaloriesStepper value={value.cal} onChange={(v) => set("cal", v)} />
      )}
    </>
  );
};
